import { useCallback, useEffect, useState } from 'react';
import {
  createSystemApiKey,
  listSystemApiKeys,
  revokeSystemApiKey,
  type SystemApiKeyView,
} from '@/services/authApi';
import './SystemApiKeys.css';

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function parseScopesInput(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function statusLabel(status: string): string {
  if (status === 'active') return '有效';
  if (status === 'revoked') return '已吊销';
  return status;
}

export function SystemApiKeys() {
  const [keys, setKeys] = useState<SystemApiKeyView[]>([]);
  const [name, setName] = useState('default');
  const [scopesInput, setScopesInput] = useState('');
  const [expiresDaysInput, setExpiresDaysInput] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const refreshKeys = useCallback(async () => {
    setError(null);
    try {
      const rows = await listSystemApiKeys();
      setKeys(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refreshKeys();
  }, [refreshKeys]);

  const onCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);
    setCreatedKey(null);
    setCopyState('idle');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('请填写 Key 名称');
      return;
    }

    const expiresRaw = expiresDaysInput.trim();
    let expiresDays: number | undefined;
    if (expiresRaw) {
      const parsed = Number(expiresRaw);
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > 3650) {
        setError('有效期需为 1–3650 天，或留空表示永不过期');
        return;
      }
      expiresDays = Math.floor(parsed);
    }

    setBusy(true);
    try {
      const result = await createSystemApiKey({
        name: trimmedName,
        scopes: parseScopesInput(scopesInput),
        expires_days: expiresDays ?? null,
      });
      setCreatedKey(result.api_key);
      setInfo(`已创建 API Key「${result.key.name}」`);
      setName('default');
      setScopesInput('');
      setExpiresDaysInput('');
      await refreshKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopyCreatedKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  const onRevoke = async (key: SystemApiKeyView) => {
    if (key.status === 'revoked') return;
    const confirmed = window.confirm(`确定吊销 API Key「${key.name}」（${key.key_prefix}…）？此操作不可撤销。`);
    if (!confirmed) return;

    setRevokingId(key.id);
    setError(null);
    setInfo(null);
    try {
      await revokeSystemApiKey(key.id);
      setInfo(`已吊销「${key.name}」`);
      await refreshKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="system-api-keys">
      <h2>系统访问 API Key</h2>
      <p className="lead">
        用于 Bot、脚本或外部程序调用套利系统 API（非交易所密钥）。请求时在 Header 中携带{' '}
        <code>x-api-key: &lt;your-key&gt;</code>。明文 Key 仅在创建时展示一次，请妥善保存。
      </p>

      <form onSubmit={(e) => void onCreate(e)}>
        <div className="create-form">
          <label>
            <span>名称</span>
            <input
              value={name}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
              placeholder="例如 basic-bot"
            />
          </label>
          <label>
            <span>有效期（天，可选）</span>
            <input
              type="number"
              min="1"
              max="3650"
              value={expiresDaysInput}
              autoComplete="off"
              onChange={(e) => setExpiresDaysInput(e.target.value)}
              placeholder="留空表示不过期"
            />
          </label>
          <label className="wide">
            <span>Scopes（可选，空格或逗号分隔）</span>
            <input
              value={scopesInput}
              autoComplete="off"
              onChange={(e) => setScopesInput(e.target.value)}
              placeholder="例如 trading:write"
            />
          </label>
        </div>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? '创建中…' : '创建 API Key'}
        </button>
      </form>

      {createdKey ? (
        <div className="created-key-banner">
          <strong>请立即复制并保存以下 Key，关闭页面后将无法再次查看：</strong>
          <div className="created-key-value">
            <code>{createdKey}</code>
            <button type="button" className="secondary compact" onClick={() => void onCopyCreatedKey()}>
              {copyState === 'copied' ? '已复制' : copyState === 'failed' ? '复制失败' : '复制'}
            </button>
          </div>
        </div>
      ) : null}

      {error ? <p className="fail">{error}</p> : null}
      {info ? <p className="msg">{info}</p> : null}

      <section className="keys-section">
        <div className="keys-section-head">
          <h3>已创建的 Key</h3>
          <button type="button" className="secondary compact" onClick={() => void refreshKeys()} disabled={busy}>
            刷新
          </button>
        </div>

        {!keys.length ? (
          <p className="keys-empty">暂无 API Key</p>
        ) : (
          <div className="keys-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>名称</th>
                  <th>前缀</th>
                  <th>状态</th>
                  <th>Scopes</th>
                  <th>创建时间</th>
                  <th>过期时间</th>
                  <th>最近使用</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td>
                      <code>{key.key_prefix}…</code>
                    </td>
                    <td>
                      <span className={`status-badge ${key.status}`}>{statusLabel(key.status)}</span>
                    </td>
                    <td className="scopes-cell">{key.scopes.length ? key.scopes.join(', ') : '—'}</td>
                    <td className="meta-cell">{formatDateTime(key.created_at)}</td>
                    <td className="meta-cell">{formatDateTime(key.expires_at)}</td>
                    <td className="meta-cell">{formatDateTime(key.last_used_at)}</td>
                    <td>
                      {key.status === 'revoked' ? null : (
                        <button
                          type="button"
                          className="secondary compact danger"
                          disabled={revokingId === key.id}
                          onClick={() => void onRevoke(key)}
                        >
                          {revokingId === key.id ? '吊销中…' : '吊销'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
